import React, { useEffect, useMemo, useState } from "react";
import { FaHeadset, FaPaperclip, FaPaperPlane, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import api from "../api";
import { useAuth } from "../AuthContext";
import "./TechnicalSupport.css";

const getEmailDomain = (email = "") => String(email).split("@")[1]?.toLowerCase() || "não identificado";

const statusLabel = (ticket) => {
  const status = String(ticket.mailStatus || (ticket.mailError ? "FAILED" : "PENDING")).toUpperCase();
  switch (status) {
    case "SENT": return "Aceito pelo servidor SMTP (entrega à caixa não confirmada)";
    case "QUEUED":
    case "PENDING": return "Aguardando processamento";
    case "PROCESSING": return "Em envio pelo servidor";
    case "RETRY": return "Falhou temporariamente: nova tentativa agendada";
    case "FAILED": return "Falha no envio da notificação";
    default: return "Status da notificação indisponível";
  }
};

export default function TechnicalSupport() {
  const { user } = useAuth();
  const [form, setForm] = useState({ type: "problema", subject: "", message: "" });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [tab, setTab] = useState("new");
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState("");
  const [retryingId, setRetryingId] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!user?.isMaster || tab !== "history") return;
    let mounted = true;
    setTicketsLoading(true);
    setTicketsError("");
    api.get("/support/tickets").then(response => {
      if (mounted) setTickets(response.data?.tickets || []);
    }).catch(error => {
      if (mounted) setTicketsError(error?.response?.data?.error || "Falha ao carregar os chamados.");
    }).finally(() => { if (mounted) setTicketsLoading(false); });
    return () => { mounted = false; };
  }, [tab, user?.isMaster]);

  const resendNotification = async (ticket) => {
    setTicketsError("");
    setRetryingId(ticket.id);
    try {
      await api.post(`/support/tickets/${encodeURIComponent(ticket.id)}/resend-notification`);
      const response = await api.get("/support/tickets", { params: { _refresh: Date.now() } });
      setTickets(Array.isArray(response.data?.tickets) ? response.data.tickets : []);
      setFeedback({
        type: "success",
        message: `Notificação do protocolo ${ticket.protocol} enfileirada para suporte@nashtec.com.br e contato@nashtec.com.br. O recebimento ainda precisa ser confirmado nas caixas de destino.`,
      });
    } catch (error) {
      setTicketsError(error.response?.data?.error || "Não foi possível reenviar a notificação.");
    } finally {
      setRetryingId(null);
    }
  };

  const downloadAttachment = async (ticket, file) => {
    try {
      const response = await api.get(`/support/tickets/${ticket.id}/attachments/${file.index}`, { responseType: "blob" });
      const href = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = href;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(href), 30000);
    } catch {
      setTicketsError("Não foi possível baixar o anexo.");
    }
  };

  const visibleTickets = tickets.filter(ticket =>
    [ticket.protocol, ticket.subject, ticket.message, ticket.userName, ticket.userEmail, ticket.accessDomain]
      .some(value => String(value || "").toLocaleLowerCase("pt-BR").includes(search.toLocaleLowerCase("pt-BR")))
  );

  const userName = user?.name || user?.email || "Usuário autenticado";
  const userEmail = user?.email || "";
  const emailDomain = useMemo(() => getEmailDomain(userEmail), [userEmail]);
  const accessDomain = typeof window !== "undefined" ? window.location.hostname : "não identificado";

  const updateField = (field, value) => {
    setFeedback(null);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onFileChange = (event) => {
    setFeedback(null);
    setFiles(Array.from(event.target.files || []));
  };

  const submit = async (event) => {
    event.preventDefault();
    setFeedback(null);
    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("type", form.type);
      payload.append("subject", form.subject);
      payload.append("message", form.message);
      files.forEach((file) => payload.append("attachments", file));
      const response = await api.post("/support/tickets", payload, { headers: { "Content-Type": "multipart/form-data" } });
      const notificationFailed = response.data?.notificationStatus === "FAILED";
      const detail = notificationFailed
        ? "O chamado foi registrado, mas o envio da notificação falhou."
        : "O chamado foi registrado e a notificação foi colocada na fila de e-mail para suporte e contato. A entrega à caixa ainda não foi confirmada.";
      setFeedback({ type: notificationFailed ? "error" : "success",
        message: `${response.data?.warning || detail} Protocolo: ${response.data?.protocol}` });
      setForm({ type: "problema", subject: "", message: "" });
      setFiles([]);
      event.target.reset();
    } catch (error) {
      setFeedback({ type: "error", message: error.response?.data?.error || "Não foi possível enviar o relato ao suporte." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="support-page">
      <div className="support-page__hero">
        <div className="support-page__icon"><FaHeadset /></div>
        <div>
          <p>Central NashTec</p>
          <h1>Suporte Técnico</h1>
          <span>Relate problemas ou envie sugestões para evolução do sistema.</span>
        </div>
      </div>

      {feedback && (
        <div className={`support-page__feedback support-page__feedback--${feedback.type}`}>
          {feedback.type === "success" ? <FaCheckCircle /> : <FaExclamationTriangle />}
          {feedback.message}
        </div>
      )}

      {user?.isMaster && (
        <nav className="support-page__tabs" aria-label="Abas do Suporte Técnico">
          <button type="button" className={tab === "new" ? "active" : ""} onClick={() => setTab("new")}>Novo chamado</button>
          <button type="button" className={tab === "history" ? "active" : ""} onClick={() => setTab("history")}>Chamados recebidos (master)</button>
        </nav>
      )}
      {tab === "history" && user?.isMaster ? (
        <section className="support-page__card">
          <h2>Histórico de Suporte Técnico</h2>
          <p>Chamados registrados neste sistema. O status de e-mail é independente do registro do chamado.</p>
          <label>Buscar por protocolo, usuário, assunto ou conteúdo
            <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar chamados..." />
          </label>
          {ticketsLoading && <p>Carregando chamados...</p>}
          {ticketsError && <p role="alert">{ticketsError}</p>}
          {!ticketsLoading && <p>{visibleTickets.length} chamado(s) encontrado(s).</p>}
          <div className="support-page__tickets">
            {visibleTickets.map(ticket => (
              <article className="support-page__ticket" key={ticket.id}>
                <div className="support-page__ticket-head"><strong>{ticket.protocol}</strong><span>{new Date(ticket.createdAt).toLocaleString("pt-BR")}</span></div>
                <h3>{ticket.subject}</h3>
                <p><strong>Tipo:</strong> {ticket.type === "sugestao" ? "Sugestão" : "Problema"}</p>
                <p><strong>Enviado por:</strong> {ticket.userName} ({ticket.userEmail})</p>
                <p><strong>Sistema:</strong> {ticket.accessDomain}</p>
                <p><strong>Notificação:</strong> {statusLabel(ticket)}</p>
                {!String(ticket.id).startsWith("legacy-") && (
                  <button className="support-page__resend" type="button" disabled={retryingId !== null}
                    onClick={() => resendNotification(ticket)}>
                    <FaPaperPlane /> {retryingId === ticket.id ? "Reenviando..." : "Reenviar e-mail para suporte e contato"}
                  </button>
                )}
                {(ticket.mailLastError || ticket.mailError) && <p><strong>Erro do e-mail:</strong> {ticket.mailLastError || ticket.mailError}</p>}
                <div className="support-page__ticket-message">{ticket.message}</div>
                {ticket.attachments?.length > 0 && <div className="support-page__ticket-files">
                  {ticket.attachments.map(file => <button type="button" key={file.index} onClick={() => downloadAttachment(ticket, file)}><FaPaperclip /> {file.name}</button>)}
                </div>}
              </article>
            ))}
          </div>
        </section>
      ) : <form className="support-page__card" onSubmit={submit}>
        <div className="support-page__grid">
          <label>
            Nome do usuário
            <input value={userName} disabled readOnly />
          </label>
          <label>
            E-mail
            <input value={userEmail} disabled readOnly />
          </label>
          <label>
            Domínio do e-mail
            <input value={emailDomain} disabled readOnly />
          </label>
          <label>
            Domínio de acesso
            <input value={accessDomain} disabled readOnly />
          </label>
          <label>
            Tipo de contato
            <select value={form.type} onChange={(event) => updateField("type", event.target.value)}>
              <option value="problema">Relatar problema</option>
              <option value="sugestao">Dar sugestão</option>
            </select>
          </label>
          <label>
            Assunto
            <input value={form.subject} onChange={(event) => updateField("subject", event.target.value)} placeholder="Ex.: erro ao abrir solicitação" required />
          </label>
        </div>

        <label className="support-page__full">
          Descrição
          <textarea value={form.message} onChange={(event) => updateField("message", event.target.value)} placeholder="Descreva o que aconteceu, os passos realizados ou a melhoria sugerida." required rows={8} />
        </label>

        <label className="support-page__upload">
          <FaPaperclip /> Anexar fotos, vídeos ou documentos
          <input type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip" onChange={onFileChange} />
        </label>

        {files.length > 0 && (
          <ul className="support-page__files">
            {files.map((file) => <li key={`${file.name}-${file.size}`}>{file.name} <span>{(file.size / 1024).toFixed(1)} KB</span></li>)}
          </ul>
        )}

        <button className="support-page__submit" type="submit" disabled={loading}>
          <FaPaperPlane /> {loading ? "Enviando..." : "Enviar relato e gerar protocolo"}
        </button>
      </form>}
    </section>
  );
}
